import { useFocusable, getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';

import useCollections from '@smart-tv/hooks/use-collections';
import useDialog from '@smart-tv/hooks/use-dialog';
import useNetwork, { isOfflineAtom } from '@smart-tv/hooks/use-network/use-network';
import { cancelAnimatedScroll } from '../_utils/utils';
import { PageType } from '../_utils/types';
import { pageTypeAtom } from '@smart-tv/atoms';
import { UIElement } from '@smart-tv/typings/content';

interface UseStripesBaseOptions {
  pageType: PageType;
  stripesStore: {
    setActiveUIElement: (uiElement: UIElement) => void;
  };
  onBeforeShowAll?: () => void;
}

const pageTypeToString = (pageType: PageType): 'universe' | 'frontpage' | 'provider-universe' => {
  if (pageType === PageType.Universe) return 'universe';
  if (pageType === PageType.FrontPage) return 'frontpage';
  return 'provider-universe';
};

const useStripesBase = ({ pageType, stripesStore, onBeforeShowAll }: UseStripesBaseOptions) => {
  const { ref, focusKey } = useFocusable({ saveLastFocusedChild: false });
  const { opened: openDialog } = useDialog();
  const { openCollections, handleOpen } = useCollections();
  const { checkAndUpdateNetwork, setIsNetworkDialogSuppressed } = useNetwork();
  const setIsOffline = useSetAtom(isOfflineAtom);
  const setPageType = useSetAtom(pageTypeAtom);

  const savedFocusKeyBeforeError = useRef<string | null>(null);
  const savedUIElementBeforeError = useRef<UIElement | null>(null);

  useEffect(() => {
    setPageType(pageTypeToString(pageType));
  }, [pageType, setPageType]);

  useEffect(() => {
    return () => {
      cancelAnimatedScroll();
    };
  }, []);

  const handleonShowAllEnterPress = useCallback(
    async (uiElement: UIElement) => {
      onBeforeShowAll?.();

      savedFocusKeyBeforeError.current = getCurrentFocusKey();
      savedUIElementBeforeError.current = uiElement;

      setIsNetworkDialogSuppressed(false);
      setIsOffline(false);
      const hasNetwork = await checkAndUpdateNetwork();

      if (!hasNetwork) return;
      stripesStore.setActiveUIElement(uiElement);
      handleOpen();
    },
    [
      checkAndUpdateNetwork,
      handleOpen,
      onBeforeShowAll,
      setIsOffline,
      setIsNetworkDialogSuppressed,
      stripesStore
    ]
  );

  return {
    ref,
    focusKey,
    openDialog,
    openCollections,
    handleOpen,
    handleonShowAllEnterPress,
    savedFocusKeyBeforeError,
    savedUIElementBeforeError,
    checkAndUpdateNetwork,
    setIsOffline
  };
};

export default useStripesBase;
